import { motion } from "framer-motion";
import { Droplets, Zap, Home } from "lucide-react";
import { useInView } from "@/hooks";
import { problemService } from "@/services/firebase";
import { useState, useEffect } from "react";
import type { ProblemSection as ProblemType } from "@/types";

const iconMap: Record<string, React.ElementType> = {
  droplet: Droplets,
  zap: Zap,
  home: Home,
};

export function ProblemSection() {
  const { ref, isInView } = useInView();
  const [data, setData] = useState<ProblemType | null>(null);

  useEffect(() => {
    problemService.getAll().then((items) => setData(items[0] || null));
  }, []);

  return (
    <section id="problem" ref={ref} className="py-20 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-4">
            {data?.title || "Un problème quotidien qui touche des millions de personnes."}
          </h2>
          <p className="text-lg text-gray-500">
            {data?.subtitle || "Vous avez déjà vécu l'une de ces situations ?"}
          </p>
        </motion.div>

        {/* Scenarios */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {(data?.scenarios || []).map((scenario, index) => {
            const Icon = iconMap[scenario.icon] || Droplets;
            return (
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="bg-white rounded-2xl p-8 shadow-card hover:shadow-lg transition-shadow border border-gray-100"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-dark mb-3">{scenario.title}</h3>
                <p className="text-gray-500 leading-relaxed text-sm">{scenario.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Narrative */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="bg-white rounded-2xl p-8 md:p-10 shadow-card mb-12"
        >
          <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed whitespace-pre-line">
            {data?.narrativeText || ""}
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6">
          {(data?.stats || []).map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="bg-dark rounded-2xl p-8 text-center"
            >
              <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
              <div className="text-white/70 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
